import { useSelector } from 'react-redux';
import { getToken } from '../../../../../features/token/tokenSaver';

const STORAGE_META = 'invert_new_campaign_meta';
const STORAGE_H_TOKEN = 'invert_new_campaign_horizontal_token';
const STORAGE_V_TOKEN = 'invert_new_campaign_vertical_token';
const STORAGE_RENEW_AD_ID = 'invert_renew_campaign_ad_id';

export default function PaymentButton({
    setPendingRedirect,
    data: formData,
    cost,
    onCheckoutError,
    tosAccepted,
    onTosError,
    billingMode = 'one_time',
    advertisementId = null,
}) {
    const token = useSelector(getToken);
    const effectiveMonthlyFee = cost !== undefined && cost !== null && cost !== ''
        ? Number(cost)
        : Number(formData?.monthlyFee ?? 0);
    const normalizedMonthlyFee = Number.isFinite(effectiveMonthlyFee) && effectiveMonthlyFee > 0
        ? effectiveMonthlyFee.toFixed(2)
        : '500.00';

    const handleStripeCheckout = async () => {
        const reportError = (message) => {
            if (onCheckoutError) {
                onCheckoutError(message);
            } else {
                window.alert(message);
            }
        };

        if (!tosAccepted) {
            const tosMessage = 'You must agree to the INVERT Advertising Agreement before proceeding to payment.';
            if (onTosError) {
                onTosError(tosMessage);
            } else {
                window.alert(tosMessage);
            }
            return;
        }

        const isPrepay = billingMode === 'one_time_prepay';
        const isRenew = billingMode === 'one_time_renew';
        const screenIds = formData?.selectedScreenIds || [];
        const renewAdId = advertisementId || formData?.advertisementId;

        if (isRenew && !renewAdId) {
            reportError('Missing campaign to renew.');
            return;
        }

        if (!isRenew && !screenIds.length) {
            reportError('Select at least one screen before payment.');
            return;
        }

        if (!isPrepay && !isRenew && (!formData?.campaignHorizontalContent || !formData?.campaignVerticalContent)) {
            reportError('Campaign creative data is missing. Please go back and upload both assets.');
            return;
        }

        const metaPayload = {
            campaignName: formData.campaignName,
            monthlyFee: Number(normalizedMonthlyFee),
            selectedScreenIds: screenIds,
            storefront: formData.storefront,
            categoryId: formData.categoryId,
            campaignViewingTime: formData.campaignViewingTime,
            campaignBudget: formData.campaignBudget,
            totalCampaignImpressions: formData.totalCampaignImpressions,
            costPerImpression: formData.costPerImpression,
            selectedLocations: formData.selectedLocations,
            billingMode,
            advertisementId: renewAdId || null,
        };

        try {
            setPendingRedirect(true);

            let horizontalCreativeToken = null;
            let verticalCreativeToken = null;

            if (!isPrepay && !isRenew) {
                const stageRes = await fetch(
                    `${process.env.REACT_APP_API_URL}/advertiser/campaign-stripe/stage-creatives`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: token,
                        },
                        body: JSON.stringify({
                            campaignHorizontalContent: formData.campaignHorizontalContent,
                            campaignVerticalContent: formData.campaignVerticalContent,
                        }),
                    }
                );

                const stageData = await stageRes.json().catch(() => ({}));
                if (!stageRes.ok) {
                    throw new Error(stageData.error || 'Failed to prepare creatives for checkout');
                }
                if (!stageData.horizontalCreativeToken || !stageData.verticalCreativeToken) {
                    throw new Error('Invalid response when saving creatives');
                }
                horizontalCreativeToken = stageData.horizontalCreativeToken;
                verticalCreativeToken = stageData.verticalCreativeToken;
            }

            try {
                sessionStorage.setItem(STORAGE_META, JSON.stringify(metaPayload));
                if (isRenew && renewAdId) {
                    sessionStorage.setItem(STORAGE_RENEW_AD_ID, String(renewAdId));
                } else {
                    sessionStorage.removeItem(STORAGE_RENEW_AD_ID);
                }
                if (horizontalCreativeToken && verticalCreativeToken) {
                    sessionStorage.setItem(STORAGE_H_TOKEN, horizontalCreativeToken);
                    sessionStorage.setItem(STORAGE_V_TOKEN, verticalCreativeToken);
                } else {
                    sessionStorage.removeItem(STORAGE_H_TOKEN);
                    sessionStorage.removeItem(STORAGE_V_TOKEN);
                }
                sessionStorage.removeItem('invert_new_campaign_horizontal');
                sessionStorage.removeItem('invert_new_campaign_vertical');
            } catch (storageErr) {
                console.warn('Could not persist campaign checkout metadata:', storageErr);
                setPendingRedirect(false);
                reportError(
                    'Could not save checkout session data. Try again, or use a private/incognito window with cookies enabled.'
                );
                return;
            }

            const checkoutBody = {
                billingMode,
            };
            if (isRenew) {
                checkoutBody.advertisementId = renewAdId;
            } else {
                checkoutBody.screenIds = screenIds;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/advertiser/campaign-stripe/create-session`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token,
                    },
                    body: JSON.stringify(checkoutBody),
                }
            );

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || 'Failed to start checkout');
            }
            if (!data.url) {
                throw new Error('Invalid checkout response');
            }

            window.location.href = data.url;
        } catch (err) {
            console.error('Stripe checkout error:', err);
            setPendingRedirect(false);
            reportError(err.message || 'Stripe checkout failed');
        }
    };

    const payLabel = billingMode === 'one_time_renew'
        ? `Renew for $${normalizedMonthlyFee} with card (Stripe)`
        : `Pay $${normalizedMonthlyFee} with card (Stripe)`;

    return (
        <div style={{ maxWidth: '750px', minHeight: '120px' }}>
            <button
                type="button"
                className="advertiserRegisterSubmitBtn"
                onClick={handleStripeCheckout}
                style={{ width: '100%', marginBottom: '0.75rem' }}
            >
                {payLabel}
            </button>
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#555', margin: 0 }}>
                {billingMode === 'one_time_renew'
                    ? 'Secure one-time payment. Your campaign will restart for another month after payment.'
                    : 'Secure one-time payment. You will return here to finish submitting your campaign.'}
            </p>
        </div>
    );
}
